import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SIDE_PANE_MAX_WIDTH,
  SIDE_PANE_MIN_WIDTH,
  SIDE_PANE_WIDTH,
  remToPx,
} from '../constants/layout';

/**
 * ONE side pane's geometry: folded or not, how wide, and the drag that changes it.
 *
 * Both workstation panes run this same hook, which is the whole point — the owner asked for panes that are
 * the same width and behave the same way, and two hand-rolled implementations is exactly how they drifted
 * apart the first time. Everything a pane can remember (folded state, dragged width) is persisted, because a
 * creator who narrowed their explorer did not mean "until the next reload".
 */

/** Everything the resize handle needs; spread straight onto it (see PaneResizeHandle). */
export interface PaneResizeProps {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
  onDoubleClick: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  dragging: boolean;
}

export interface SidePaneState {
  collapsed: boolean;
  toggle: () => void;
  /** A CSS length — the shared token until the creator drags, then their own px width. */
  width: string;
  dragging: boolean;
  /** Put this on the element that IS the pane; the drag measures its real box rather than guessing rems. */
  paneRef: React.RefObject<HTMLDivElement>;
  resize: PaneResizeProps;
}

interface Keys {
  /** localStorage key for the folded flag (the left pane keeps its historical key). */
  collapsed: string;
  /** localStorage key for the dragged width, in px. */
  width: string;
}

const KEYBOARD_STEP = 16; // px per arrow press — a nudge, not a jump

const readWidth = (key: string): number | null => {
  try {
    const raw = localStorage.getItem(key);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

export function useSidePane(side: 'left' | 'right', keys: Keys): SidePaneState {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(keys.collapsed) === '1'; } catch { return false; }
  });
  const [width, setWidth] = useState<number | null>(() => readWidth(keys.width));
  const [dragging, setDragging] = useState(false);
  const paneRef = useRef<HTMLDivElement>(null);
  // Where the pointer went down and how wide the pane was then — a drag is a delta, so a pane that starts at
  // the token width needs its MEASURED px, not a re-derived rem guess.
  const origin = useRef({ x: 0, w: 0 });

  useEffect(() => {
    try { localStorage.setItem(keys.collapsed, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed, keys.collapsed]);

  const persist = useCallback((px: number | null) => {
    try {
      if (px === null) localStorage.removeItem(keys.width);
      else localStorage.setItem(keys.width, String(Math.round(px)));
    } catch { /* ignore */ }
  }, [keys.width]);

  const clamp = useCallback((px: number) => (
    Math.min(remToPx(SIDE_PANE_MAX_WIDTH), Math.max(remToPx(SIDE_PANE_MIN_WIDTH), px))
  ), []);

  const measured = useCallback(
    () => paneRef.current?.getBoundingClientRect().width || remToPx(SIDE_PANE_WIDTH),
    [],
  );

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    // Stop the browser turning the drag into a text selection / native scroll gesture.
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    origin.current = { x: e.clientX, w: measured() };
    setDragging(true);
  }, [measured]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!dragging) return;
    // The handle is on the pane's INNER edge, so the left pane grows rightwards and the right pane leftwards.
    const delta = side === 'left' ? e.clientX - origin.current.x : origin.current.x - e.clientX;
    setWidth(clamp(origin.current.w + delta));
  }, [dragging, side, clamp]);

  const endDrag = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!dragging) return;
    setDragging(false);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    // Write once at the end rather than on every pointer move — a drag is hundreds of events.
    setWidth((w) => { persist(w); return w; });
  }, [dragging, persist]);

  /** Double-click the border → back to the shared default, the VS Code gesture creators already know. */
  const onDoubleClick = useCallback(() => { setWidth(null); persist(null); }, [persist]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    const dir = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    if (!dir) return;
    e.preventDefault();
    const step = side === 'left' ? dir * KEYBOARD_STEP : -dir * KEYBOARD_STEP;
    const next = clamp(measured() + step);
    setWidth(next);
    persist(next);
  }, [side, clamp, measured, persist]);

  // While dragging, the whole document adopts the resize cursor and stops selecting text — otherwise the
  // pointer picks up chat messages on its way across them.
  useEffect(() => {
    if (!dragging) return;
    const { body } = document;
    const prevSelect = body.style.userSelect;
    const prevCursor = body.style.cursor;
    body.style.userSelect = 'none';
    body.style.cursor = 'col-resize';
    return () => { body.style.userSelect = prevSelect; body.style.cursor = prevCursor; };
  }, [dragging]);

  return {
    collapsed,
    toggle: useCallback(() => setCollapsed((c) => !c), []),
    width: width === null ? SIDE_PANE_WIDTH : `${width}px`,
    dragging,
    paneRef,
    resize: { onPointerDown, onPointerMove, onPointerUp: endDrag, onPointerCancel: endDrag, onDoubleClick, onKeyDown, dragging },
  };
}

export default useSidePane;
